/**
 * Price Action overlay renderer — pin bar / engulfing markers, swing pivots,
 * and labelled supply / demand zones.
 */
import type { PriceActionOverlay } from '@trading/shared';
import type { OverlayDrawContext } from './types';

function drawPatternMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
  bias: 'bullish' | 'bearish',
  label: string,
): void {
  ctx.save();
  // Triangle pointing up for bullish, down for bearish.
  ctx.fillStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = 6;
  ctx.beginPath();
  if (bias === 'bullish') {
    ctx.moveTo(x, y + 8);
    ctx.lineTo(x - 6, y + 18);
    ctx.lineTo(x + 6, y + 18);
  } else {
    ctx.moveTo(x, y - 8);
    ctx.lineTo(x - 6, y - 18);
    ctx.lineTo(x + 6, y - 18);
  }
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.font = 'bold 9px Inter, sans-serif';
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = bias === 'bullish' ? 'top' : 'bottom';
  ctx.fillText(label, x, bias === 'bullish' ? y + 22 : y - 22);
  ctx.restore();
}

export function drawPriceActionOverlay(data: PriceActionOverlay, c: OverlayDrawContext): void {
  const { ctx, height, defaultZoneStartX, zoneEndX, getYFromPrice, getXFromTime } = c;

  // Supply zones — soft red bands.
  data.supply?.forEach((zone) => {
    const topY = getYFromPrice(zone.top);
    const bottomY = getYFromPrice(zone.bottom);
    if (topY === null || bottomY === null) return;
    const y = Math.min(topY, bottomY);
    const h = Math.abs(bottomY - topY);
    if (h < 1) return;
    const startX = getXFromTime(zone.time, defaultZoneStartX);
    const w = Math.max(zoneEndX - startX, 80);
    ctx.save();
    const alpha = zone.status === 'fresh' ? 0.14 : zone.status === 'tested' ? 0.08 : 0.04;
    ctx.fillStyle = `#ef4444${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
    ctx.fillRect(startX, y, w, h);
    ctx.fillStyle = '#ef4444cc';
    ctx.fillRect(startX, y, 2, h);
    ctx.font = 'bold 10px Inter, sans-serif';
    ctx.fillStyle = '#ef4444';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('SUPPLY', startX + 6, y + 4);
    ctx.restore();
  });

  // Demand zones — soft green bands.
  data.demand?.forEach((zone) => {
    const topY = getYFromPrice(zone.top);
    const bottomY = getYFromPrice(zone.bottom);
    if (topY === null || bottomY === null) return;
    const y = Math.min(topY, bottomY);
    const h = Math.abs(bottomY - topY);
    if (h < 1) return;
    const startX = getXFromTime(zone.time, defaultZoneStartX);
    const w = Math.max(zoneEndX - startX, 80);
    ctx.save();
    const alpha = zone.status === 'fresh' ? 0.14 : zone.status === 'tested' ? 0.08 : 0.04;
    ctx.fillStyle = `#22c55e${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
    ctx.fillRect(startX, y, w, h);
    ctx.fillStyle = '#22c55ecc';
    ctx.fillRect(startX, y, 2, h);
    ctx.font = 'bold 10px Inter, sans-serif';
    ctx.fillStyle = '#22c55e';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('DEMAND', startX + 6, y + 4);
    ctx.restore();
  });

  // Pin bars.
  data.pinBars?.forEach((m) => {
    const x = getXFromTime(m.time, defaultZoneStartX);
    const y = getYFromPrice(m.price);
    if (y === null || y < 0 || y > height) return;
    drawPatternMarker(ctx, x, y, m.bias === 'bullish' ? '#22c55e' : '#ef4444', m.bias, 'PIN');
  });

  // Engulfings.
  data.engulfings?.forEach((m) => {
    const x = getXFromTime(m.time, defaultZoneStartX);
    const y = getYFromPrice(m.price);
    if (y === null || y < 0 || y > height) return;
    drawPatternMarker(ctx, x, y, m.bias === 'bullish' ? '#3b82f6' : '#f97316', m.bias, 'ENG');
  });

  // Swing pivots — small diamond marker.
  data.pivots?.forEach((p) => {
    const x = getXFromTime(p.time, defaultZoneStartX);
    const y = getYFromPrice(p.price);
    if (y === null || y < 0 || y > height) return;
    ctx.save();
    const color = p.type === 'high' ? '#facc15' : '#a855f7';
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 4;
    ctx.beginPath();
    ctx.moveTo(x, y - 5);
    ctx.lineTo(x + 5, y);
    ctx.lineTo(x, y + 5);
    ctx.lineTo(x - 5, y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  });
}
